import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { MdAdd } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Form, useNavigate } from "react-router-dom";
import paths from "routes/paths";
import PriceListFilters from "./components/PriceListFilters";
import PriceListTable from "./components/PriceListTable";
import { GetAllPriceList } from "./config/PriceApi";
import { FiDownloadCloud } from "react-icons/fi";
import { toast } from "react-toastify";
import { handlePriceReset } from "./config/PriceSlice";
import { handleDownloadXLSX } from "helpers/excel";
import moment from "moment";

const PriceList = () => {
  const dispatch = useDispatch();
  const { priceListData, priceFilters } = useSelector((state) => state.price);
  const { allPermissionType } = useSelector((state) => state.role);
  const priceSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Price"
  );
  const selectBranch = JSON.parse(localStorage.getItem("selectedBranch"));

  const { control, watch, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      branch: "",
      payer: "",
      nphies_code: "",
      provider_code: "",
      is_activate: false,
    },
  });

  const navigate = useNavigate();

  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const requestBody = (customPage = page, customLimit = rowsPerPage) => ({
    branch_id:
      priceFilters?.branch?.branch_id ||
      selectBranch?.branch_id ||
      selectBranch?.value ||
      "",
    nphies_code: priceFilters?.nphies_code || "",
    payer_id: priceFilters?.payer?.payer_id || "",
    provider_cde: priceFilters?.provider_code || "",
    is_activate: priceFilters?.active || false,
    start_point: customPage,
    limit: customLimit,
  });

  const fetchPriceList = async (body = {}) => {
    try {
      const response = await dispatch(GetAllPriceList(body)).unwrap();
      setTotalRows(response?.total_rows || 0);
    } catch (error) {
      toast.error("Something Went Wrong");
    }
  };

  useEffect(() => {
    const body = requestBody(page, rowsPerPage);
    fetchPriceList(body);
  }, [page, rowsPerPage]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const handleCheckListFilter = () => {
    const newPage = 0;
    setPage(newPage); // Reset page
    const body = requestBody(newPage, rowsPerPage);
    fetchPriceList(body);
  };

  const handleReset = async () => {
    const body = {
      branch_id: "",
      is_activate: false,
      nphies_code: "",
      payer_id: "",
      provider_code: "",
      start_point: 0,
      limit: 10,
    };
    setPage(0);
    setRowsPerPage(10);
    reset();
    dispatch(handlePriceReset());
    // const resetBody = requestBody(0, 10); // Fresh filters
    await fetchPriceList(body);
  };

  const handleDownloadExcel = async () => {
    const excelBody = requestBody(page, totalRows);
    await fetchPriceList(excelBody);

    const flattenedArray =
      priceListData &&
      priceListData.length > 0 &&
      priceListData.map((row, index) => [
        page * rowsPerPage + index + 1 || "",
        row?.payer_name || "", // ✅ Added License No
        row?.provider_code || "",
        row?.provider_codedesc || "", // ✅ Fixed typo from "fisrtname"
        row?.nphies_code || "",
        row?.nphies_desc || "",
        row?.unit_price || "",
        row?.price_discount || "",
        row?.price_tax || "",
        row?.activated_date
          ? moment(row?.activated_date).format("YYYY-MM-DD")
          : "" || "",
        row?.brname || "",
      ]);

    handleDownloadXLSX(
      [
        [
          "No.",
          "Payer",
          "Provider Code",
          "Provider Description",
          "NPHIES Code",
          "NPHIES Description",
          "Price",
          "Discount %",
          "VAT",
          "Activation Date",
          "Branch",
        ],
        ...(flattenedArray || []), // ✅ Correctly spread the data
      ],
      `Price_List_Export_${moment().format("DD-MM-YYYY_hh:mm:ss")}`
    );
  };

  return (
    <>
      {Number(priceSetting?.list) === 1 ? (
        <Form
          noValidate
          onSubmit={handleSubmit(handleCheckListFilter)}
          className="p-0"
        >
          <FalconComponentCard
            className="shadow-md"
            style={{ position: "relative" }}
          >
            <FalconComponentCard.Header
              className="d-flex  fixed-header justify-content-between align-items-center"
              sx={{
                position: "sticky !important",
                top: 68,
                zIndex: 1020,
                backgroundColor: "white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Col xs="auto m-0">
                <h5 className="text-primary mb-0 fs-10">Price</h5>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Button size="sm" variant="outline-danger" onClick={handleReset}>
                  Reset
                </Button>
                <Button type="submit" size="sm" variant="success">
                  Filter
                </Button>
                {Number(priceSetting?.add) === 1 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(paths.addPriceList)}
                  >
                    <MdAdd />
                    New
                  </Button>
                )}
                {Number(priceSetting?.export) === 1 && (
                  <Button size="sm" variant="warning" onClick={handleDownloadExcel}>
                    <FiDownloadCloud size={16} />
                    Excel
                  </Button>
                )}
              </Col>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body
              style={{
                marginTop: "40px", // Adjust this value based on header height
                overflowY: "auto",
                maxHeight: "calc(100vh - 200px)", // Adjust based on your needs
              }}
            >
              <PriceListFilters
                watch={watch}
                setValue={setValue}
                control={control}
              />
              <PriceListTable
                data={priceListData}
                totalRows={totalRows}
                handlePageChange={handlePageChange}
                setting={priceSetting}
                handlePerRowsChange={handlePerRowsChange}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Form>
      ) : (
        <div className="py-6 text-center border rounded-3mb-3">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to view Price list.
          </p>
        </div>
      )}
    </>
  );
};

export default PriceList;
